/**
 * The `data-*` subset of a caller-supplied attribute map, so an atom can carry a
 * declarative hook onto its rendered element — a modal-trigger marker like
 * `data-iframe-modal` / `data-iframe-src`, a test id — WITHOUT opening a generic
 * attribute spread.
 *
 * ⚠ `data-` prefix only, and this is deliberate. A `className`, `style`, an
 * `onClick` or an `aria-*` passed here is DROPPED: the escape hatch is for
 * declarative data attributes, not a way to restyle the atom (the house rule
 * forbids styling through a call site) or to bolt behaviour onto it. Non-string
 * values are dropped too — React stringifies `{}` to `"[object Object]"` on the
 * DOM, which is never what a data attribute wants.
 *
 * The result is spread BEFORE an atom's own explicit attributes, so a caller can
 * never clobber the reporting attributes (`data-linkposition` and the rest) the
 * atom sets for GTM.
 */
export function pickDataAttributes(
  input?: Record<string, unknown>
): Record<string, string> {
  if (!input) {
    return {};
  }

  const out: Record<string, string> = {};
  for (const key of Object.keys(input)) {
    const value = input[key];
    /* `data-` then at least one word/hyphen character — `data-iframe-modal`,
       `data-testid`. A bare `data-` or a key with anything else is not a valid
       custom data attribute and is left out. */
    if (/^data-[\w-]+$/.test(key) && typeof value === "string") {
      out[key] = value;
    }
  }
  return out;
}
